/**
 * Smoothed Moving Average (SMMA)
 *
 * Also known as Modified Moving Average (MMA) or Running Moving Average.
 * Uses Wilder smoothing: SMMA = (SMMA_prev × (N-1) + Price) / N.
 *
 * Category: Trend | Pane: Overlay
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const smmaIndicator: IndicatorDefinition = {
  id: "smma",
  name: "Smoothed Moving Average",
  shortName: "SMMA",
  category: "trend",
  pane: "overlay",
  params: [
    { key: "period", label: "Period", type: "number", default: 20, min: 1, max: 500 },
    { key: "source", label: "Source", type: "select", default: "close", options: [
      { label: "Close", value: "close" }, { label: "Open", value: "open" },
      { label: "High", value: "high" }, { label: "Low", value: "low" },
    ]},
    { key: "color", label: "Color", type: "color", default: "#00BCD4" },
  ],
  plots: [
    { key: "smma", label: "SMMA", color: "#00BCD4", style: "line", lineWidth: 2 },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { period, source = "close" } = params;
    const src = getSource(candles, source);
    const data: { time: number; value: number }[] = [];

    if (src.length < period) return [{ key: "smma", data }];

    let smma = 0;
    for (let i = 0; i < period; i++) smma += src[i];
    smma /= period;
    data.push({ time: candles[period - 1].time, value: smma });

    for (let i = period; i < src.length; i++) {
      smma = (smma * (period - 1) + src[i]) / period;
      data.push({ time: candles[i].time, value: smma });
    }

    return [{ key: "smma", data }];
  },
};
